import { ListOfBanner } from '@s4l/stayforlong/marketing/ListOfBanner'
import { useEffect, useMemo } from 'react'
import {
  BANNER_TYPES,
  type BannerTypeToInformation,
  type UseBannerOptions,
  type UseBannerReturn,
} from './types'
import { useUseCase } from '@s4l/utils/hooks/useUseCase'
import { Devices, useBrowser } from '@s4l/utils/hooks/useBrowser'
import debug from 'debug'

const log = debug('s4l:uikit:useBanner')

const LOGIN_CHANGE = 'login-change'

/**
 * Custom hook for extracting banner data with memoization
 *
 * Consolidates the repetitive banner extraction logic that was duplicated across
 * all banner components. Handles JSON parsing, banner retrieval, and information
 * extraction with proper error handling and TypeScript typing.
 *
 * @param options - Hook configuration
 * @param options.bannersJSON - The raw banner JSON data from the backend
 * @param options.bannerType - The specific banner type to extract
 *
 * @returns Object containing:
 *   - banner: The raw banner object for future use cases
 *   - bannerInformation: The processed banner information, typed based on banner type
 *
 * @example
 * ```tsx
 * // Get announcement banner data
 * const { bannerInformation } = useBanner({
 *   bannersJSON,
 *   bannerType: BANNER_TYPES.ANNOUNCEMENT
 * })
 *
 * // Get both banner and information for advanced use cases
 * const { banner, bannerInformation } = useBanner({
 *   bannersJSON,
 *   bannerType: BANNER_TYPES.HERO
 * })
 * ```
 */
export function useBanner<T extends keyof BannerTypeToInformation>({
  bannersJSON,
  bannerType,
}: UseBannerOptions<T>): UseBannerReturn<T> {
  const { device } = useBrowser()

  const {
    data: market,
    loading: marketLoading,
    error: marketError,
  } = useUseCase('GetCurrentMarketSharedUseCase', undefined)

  if (marketError) {
    log('Error in GetCurrentMarketSharedUseCase')
  }

  const {
    data: affiliate,
    loading: affiliateLoading,
    error: affiliateError,
  } = useUseCase('GetCurrentAffiliateSharedUseCase', undefined)

  if (affiliateError) {
    log('Error in GetCurrentAffiliateSharedUseCase')
  }

  const {
    data: loginStatus,
    execute: refreshLoginStatus,
    loading: loginLoading,
    error: loginError,
  } = useUseCase('GetCurrentLoginStatusAuthUseCase', undefined)

  if (loginError) {
    log('Error in GetCurrentLoginStatusAuthUseCase')
  }

  // Map device to viewport string
  const getDeviceString = (): 'desktop' | 'tablet' | 'mobile' => {
    switch (device) {
      case Devices.Mobile:
        return 'mobile'
      case Devices.Tablet:
        return 'tablet'
      case Devices.Desktop:
        return 'desktop'
      default:
        return 'mobile'
    }
  }

  const deviceString = getDeviceString()

  useEffect(() => {
    const handleLoginStateChange = () => {
      refreshLoginStatus()
    }

    window.addEventListener(LOGIN_CHANGE, handleLoginStateChange)

    return () => {
      window.removeEventListener(LOGIN_CHANGE, handleLoginStateChange)
    }
  }, [refreshLoginStatus])
  // Check if we need to wait for data to load (only for BILLBOARD)
  const needsLoadingData = bannerType === BANNER_TYPES.BILLBOARD
  const isLoadingRequiredData =
    needsLoadingData && (marketLoading || loginLoading || affiliateLoading)

  return useMemo(() => {
    // Early returns for edge cases
    if (!bannersJSON) {
      return { banner: undefined, bannerInformation: undefined }
    }

    if (isLoadingRequiredData) {
      return { banner: undefined, bannerInformation: undefined }
    }

    const listOfAllBanners = ListOfBanner.fromJSONOrEmpty(bannersJSON)
    const banner = listOfAllBanners.getBannerByType(bannerType)

    if (!banner) {
      return { banner: undefined, bannerInformation: undefined }
    }

    let bannerInformation: BannerTypeToInformation[T] | undefined

    switch (bannerType) {
      case BANNER_TYPES.ANNOUNCEMENT:
        bannerInformation = listOfAllBanners.getAnnouncementBannerInformation({
          device: deviceString,
        }) as BannerTypeToInformation[T]
        break
      case BANNER_TYPES.BILLBOARD:
        bannerInformation = listOfAllBanners.getBillboardBannerInformation({
          market,
          affiliate,
          loginStatus,
          device: deviceString,
        }) as BannerTypeToInformation[T]
        break
      case BANNER_TYPES.BLOCK:
        bannerInformation = listOfAllBanners.getBlockBannerInformation({
          device: deviceString,
        }) as BannerTypeToInformation[T]
        break
      case BANNER_TYPES.HERO:
        bannerInformation = listOfAllBanners.getHeroBannerInformation({
          device: deviceString,
        }) as BannerTypeToInformation[T]
        break
      default:
        bannerInformation = undefined
    }

    return { banner, bannerInformation }
  }, [bannersJSON, bannerType, isLoadingRequiredData, deviceString, market, affiliate, loginStatus])
}
